import * as XLSX from 'xlsx'
import { Customer, Project, Resource, ResourceRoleType, Partner } from '../types'
import { Task } from './taskService'
import { dataService } from './dataService'
import { taskService } from './taskService'
import { apiClient } from '../lib/apiClient'

export type EntityType = 'roleTypes' | 'resources' | 'customers' | 'projects' | 'tasks'

export type RowStatus = 'valid' | 'warning' | 'error'

export interface ParsedRow {
  rowIndex: number
  raw: Record<string, string>
  status: RowStatus
  errors: string[]
  warnings: string[]
  resolved?: Record<string, any>
}

export interface BulkUploadResult {
  succeeded: number
  failed: number
  failedRows: Array<{ rowIndex: number; title: string; error: string }>
}

const CURRENCIES = ['USD', 'GBP', 'INR']
const PROJECT_TYPES = ['Implementation', 'Support', 'T&M']
const PROJECT_STATUSES = ['Active', 'On Hold', 'Closed', 'Tentative']
const TASK_STATUSES = ['todo', 'in_progress', 'done', 'blocked']
const TASK_PRIORITIES = ['low', 'medium', 'high', 'critical']
const TASK_TYPES = ['milestone', 'task', 'subtask']
const INDUSTRIES = ['Technology', 'Manufacturing', 'Financial Services', 'Healthcare', 'Retail', 'Government', 'Other']
const REGIONS = ['North America', 'Europe', 'Asia Pacific', 'Latin America', 'Middle East & Africa']

// ── Template generation ───────────────────────────────────────────────────────

function makeWorkbook(headers: string[], sample: (string | number)[], notes: string[]): XLSX.WorkBook {
  const ws = XLSX.utils.aoa_to_sheet([headers, sample, notes])

  // Style header row bold (only supported in xlsx pro, but we still set fill manually via cell meta)
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1')
  ws['!cols'] = headers.map(() => ({ wch: 22 }))

  // Mark notes row lightly with a comment hint in A3
  const notesCellRef = XLSX.utils.encode_cell({ r: 2, c: 0 })
  if (ws[notesCellRef]) ws[notesCellRef].v = '--- NOTES (delete before uploading) ---'

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Data')
  return wb
}

export function generateRoleTypesTemplate(): void {
  const headers = ['title', 'description', 'cost_rate', 'billing_rate', 'currency', 'skills_required', 'active']
  const sample = ['Senior Developer', 'Backend specialist', 150, 200, 'USD', 'Python, SQL', 'TRUE']
  const notes = [
    'Required: title, cost_rate, billing_rate',
    '',
    '',
    '',
    'USD | GBP | INR',
    '',
    'TRUE or FALSE',
  ]
  const wb = makeWorkbook(headers, sample, notes)
  XLSX.writeFile(wb, 'role_types_template.xlsx')
}

export function generateResourcesTemplate(): void {
  const headers = ['title', 'email', 'skills', 'primary_role', 'availability', 'active']
  const sample = ['Jane Smith', 'jane@example.com', 'React, TypeScript', 'Senior Developer', 8, 'TRUE']
  const notes = [
    'Required: title',
    'Optional, must be valid email',
    '',
    'Free text role name',
    'Hours per day (default 8)',
    'TRUE or FALSE',
  ]
  const wb = makeWorkbook(headers, sample, notes)
  XLSX.writeFile(wb, 'resources_template.xlsx')
}

export function generateCustomersTemplate(): void {
  const headers = ['title', 'account_manager', 'industry', 'region']
  const sample = ['Acme Corp', 'John Doe', 'Technology', 'North America']
  const notes = [
    'Required: title',
    '',
    'Technology | Manufacturing | Financial Services | Healthcare | Retail | Government | Other',
    'North America | Europe | Asia Pacific | Latin America | Middle East & Africa',
  ]
  const wb = makeWorkbook(headers, sample, notes)
  XLSX.writeFile(wb, 'customers_template.xlsx')
}

export function generateProjectsTemplate(): void {
  const headers = ['title', 'customer_name', 'start_date', 'end_date', 'currency', 'project_type', 'status', 'budget', 'description', 'technology', 'partner_name']
  const sample = ['ERP Rollout', 'Acme Corp', '2026-01-01', '2026-12-31', 'USD', 'Implementation', 'Active', 500000, 'Full ERP rollout', 'SAP', '']
  const notes = [
    'Required: title',
    'Required: must match existing customer name',
    'Required: YYYY-MM-DD',
    'Required: YYYY-MM-DD, must be after start_date',
    'USD | GBP | INR (default USD)',
    'Implementation | Support | T&M (default Implementation)',
    'Active | On Hold | Closed | Tentative (default Active)',
    'Optional number',
    '',
    '',
    'Optional: must match existing partner name',
  ]
  const wb = makeWorkbook(headers, sample, notes)
  XLSX.writeFile(wb, 'projects_template.xlsx')
}

export function generateTasksTemplate(): void {
  const headers = ['row_ref', 'project_title', 'title', 'status', 'priority', 'task_type', 'start_date', 'due_date', 'description', 'estimated_hours', 'parent_row_ref']
  const sample = ['T1', 'ERP Rollout', 'Discovery Phase', 'todo', 'high', 'task', '2026-01-01', '2026-01-31', 'Initial discovery', 40, '']
  const notes = [
    'Your label for this row (used by parent_row_ref)',
    'Required: must match existing project title',
    'Required: task title',
    'todo | in_progress | done | blocked',
    'low | medium | high | critical',
    'milestone | task | subtask (default task)',
    'Optional: YYYY-MM-DD, must be within project dates',
    'Optional: YYYY-MM-DD, must be within project dates',
    '',
    'Optional: hours (will be converted to minutes)',
    'Optional: row_ref of parent task in this file',
  ]
  const wb = makeWorkbook(headers, sample, notes)
  XLSX.writeFile(wb, 'tasks_template.xlsx')
}

// ── File parsing ──────────────────────────────────────────────────────────────

export function parseFile(file: File): Promise<Record<string, string>[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer)
        const wb = XLSX.read(data, { type: 'array', cellDates: false })
        const ws = wb.Sheets[wb.SheetNames[0]]
        const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' })

        if (rows.length < 2) {
          reject(new Error('File must have a header row and at least one data row.'))
          return
        }

        const headers = (rows[0] as string[]).map(h => String(h).trim().toLowerCase())

        // Skip rows that are the sample row or notes row (rows 2 and 3 in template)
        const dataRows = rows.slice(1).filter((row, idx) => {
          if (idx === 0) return false // sample row
          const first = String(row[0] || '').trim()
          if (first.startsWith('---')) return false // notes row
          return row.some(cell => String(cell).trim() !== '')
        })

        const result = dataRows.map(row =>
          Object.fromEntries(headers.map((h, i) => [h, String(row[i] ?? '').trim()]))
        )
        resolve(result)
      } catch (err) {
        reject(err)
      }
    }
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsArrayBuffer(file)
  })
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function normText(v: string): string { return v.trim() }
function normBool(v: string): boolean {
  return ['true', 'yes', '1'].includes(v.toLowerCase())
}
function normDate(v: string): string | null {
  if (!v) return null
  // Handle Excel serial dates
  const num = parseFloat(v)
  if (!isNaN(num) && num > 1000) {
    const date = XLSX.SSF.parse_date_code(num)
    if (date) {
      const m = String(date.m).padStart(2, '0')
      const d = String(date.d).padStart(2, '0')
      return `${date.y}-${m}-${d}`
    }
  }
  // Already a string date
  const match = v.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/)
  if (match) return `${match[1]}-${match[2].padStart(2, '0')}-${match[3].padStart(2, '0')}`
  return null
}
function normCurrency(v: string): string {
  const u = v.toUpperCase()
  return CURRENCIES.includes(u) ? u : 'USD'
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

// ── Duplicate checking ────────────────────────────────────────────────────────

async function fetchExistingTitles(table: string, orgId: string): Promise<Set<string>> {
  const data = await apiClient.get<Array<{ title: string }>>(`/orgs/${orgId}/${table}/titles`)
  return new Set((data || []).map((r: any) => r.title.toLowerCase()))
}

// ── Row validators ────────────────────────────────────────────────────────────

export async function validateRoleTypeRows(
  rawRows: Record<string, string>[],
  orgId: string
): Promise<ParsedRow[]> {
  const existing = await fetchExistingTitles('resource_role_types', orgId)
  const batchTitles = new Set<string>()

  return rawRows.map((raw, i) => {
    const errors: string[] = []
    const warnings: string[] = []
    const title = normText(raw['title'] || '')

    if (!title) errors.push('title is required')
    else {
      const key = title.toLowerCase()
      if (existing.has(key)) errors.push(`Role type "${title}" already exists in this organisation`)
      else if (batchTitles.has(key)) errors.push(`Duplicate title "${title}" within this upload`)
      else batchTitles.add(key)
    }

    const costRate = parseFloat(raw['cost_rate'] || '0')
    const billingRate = parseFloat(raw['billing_rate'] || '0')
    if (isNaN(costRate) || costRate < 0) errors.push('cost_rate must be a non-negative number')
    if (isNaN(billingRate) || billingRate < 0) errors.push('billing_rate must be a non-negative number')

    const currency = (raw['currency'] || '').toUpperCase()
    if (currency && !CURRENCIES.includes(currency)) warnings.push(`currency "${raw['currency']}" is not recognised, will default to USD`)

    const resolved = {
      title,
      description: normText(raw['description'] || ''),
      cost_rate: costRate,
      billing_rate: billingRate,
      currency: normCurrency(raw['currency'] || 'USD'),
      skills_required: normText(raw['skills_required'] || ''),
      active: raw['active'] ? normBool(raw['active']) : true,
    }

    return { rowIndex: i + 4, raw, errors, warnings, resolved, status: errors.length ? 'error' : warnings.length ? 'warning' : 'valid' }
  })
}

export async function validateResourceRows(
  rawRows: Record<string, string>[],
  orgId: string
): Promise<ParsedRow[]> {
  const existing = await fetchExistingTitles('resources', orgId)
  const batchTitles = new Set<string>()

  return rawRows.map((raw, i) => {
    const errors: string[] = []
    const warnings: string[] = []
    const title = normText(raw['title'] || '')

    if (!title) errors.push('title is required')
    else {
      const key = title.toLowerCase()
      if (existing.has(key)) errors.push(`Resource "${title}" already exists in this organisation`)
      else if (batchTitles.has(key)) errors.push(`Duplicate title "${title}" within this upload`)
      else batchTitles.add(key)
    }

    const email = normText(raw['email'] || '')
    if (email && !isValidEmail(email)) errors.push(`email "${email}" is not a valid email address`)

    const avail = parseFloat(raw['availability'] || '8')
    if (isNaN(avail) || avail <= 0) errors.push('availability must be a positive number')

    const resolved = {
      title,
      email,
      skills: normText(raw['skills'] || ''),
      primary_role: normText(raw['primary_role'] || ''),
      availability: isNaN(avail) ? 8 : avail,
      active: raw['active'] ? normBool(raw['active']) : true,
    }

    return { rowIndex: i + 4, raw, errors, warnings, resolved, status: errors.length ? 'error' : warnings.length ? 'warning' : 'valid' }
  })
}

export async function validateCustomerRows(
  rawRows: Record<string, string>[],
  orgId: string
): Promise<ParsedRow[]> {
  const existing = await fetchExistingTitles('customers', orgId)
  const batchTitles = new Set<string>()

  return rawRows.map((raw, i) => {
    const errors: string[] = []
    const warnings: string[] = []
    const title = normText(raw['title'] || '')

    if (!title) errors.push('title is required')
    else {
      const key = title.toLowerCase()
      if (existing.has(key)) errors.push(`Customer "${title}" already exists in this organisation`)
      else if (batchTitles.has(key)) errors.push(`Duplicate title "${title}" within this upload`)
      else batchTitles.add(key)
    }

    const industry = normText(raw['industry'] || '')
    const region = normText(raw['region'] || '')

    if (industry && !INDUSTRIES.some(i => i.toLowerCase() === industry.toLowerCase())) {
      warnings.push(`industry "${industry}" is not in the standard list — will be imported as-is`)
    }
    if (region && !REGIONS.some(r => r.toLowerCase() === region.toLowerCase())) {
      warnings.push(`region "${region}" is not in the standard list — will be imported as-is`)
    }

    const resolved = {
      title,
      account_manager: normText(raw['account_manager'] || ''),
      industry,
      region,
    }

    return { rowIndex: i + 4, raw, errors, warnings, resolved, status: errors.length ? 'error' : warnings.length ? 'warning' : 'valid' }
  })
}

export async function validateProjectRows(
  rawRows: Record<string, string>[],
  orgId: string,
  customers: Customer[],
  partners: Partner[]
): Promise<ParsedRow[]> {
  const existing = await fetchExistingTitles('projects', orgId)
  const batchTitles = new Set<string>()
  const customerMap = new Map(customers.map(c => [c.title.toLowerCase(), c.id]))
  const partnerMap = new Map(partners.map(p => [p.title.toLowerCase(), p.id]))

  return rawRows.map((raw, i) => {
    const errors: string[] = []
    const warnings: string[] = []
    const title = normText(raw['title'] || '')

    if (!title) errors.push('title is required')
    else {
      const key = title.toLowerCase()
      if (existing.has(key)) errors.push(`Project "${title}" already exists in this organisation`)
      else if (batchTitles.has(key)) errors.push(`Duplicate title "${title}" within this upload`)
      else batchTitles.add(key)
    }

    const customerName = normText(raw['customer_name'] || '')
    let customerId: string | null = null
    if (!customerName) {
      errors.push('customer_name is required')
    } else {
      customerId = customerMap.get(customerName.toLowerCase()) ?? null
      if (!customerId) errors.push(`Customer "${customerName}" not found — create it first or check spelling`)
    }

    const startDate = normDate(raw['start_date'] || '')
    const endDate = normDate(raw['end_date'] || '')
    if (!startDate) errors.push('start_date is required (YYYY-MM-DD)')
    if (!endDate) errors.push('end_date is required (YYYY-MM-DD)')
    if (startDate && endDate && startDate > endDate) errors.push('start_date must be before end_date')

    const currency = (raw['currency'] || '').toUpperCase()
    if (currency && !CURRENCIES.includes(currency)) warnings.push(`currency "${raw['currency']}" not recognised, will default to USD`)

    const projectType = normText(raw['project_type'] || '')
    const matchedType = PROJECT_TYPES.find(t => t.toLowerCase() === projectType.toLowerCase())
    if (projectType && !matchedType) warnings.push(`project_type "${projectType}" not recognised, will default to Implementation`)

    const status = normText(raw['status'] || '')
    const matchedStatus = PROJECT_STATUSES.find(s => s.toLowerCase() === status.toLowerCase())
    if (status && !matchedStatus) warnings.push(`status "${status}" not recognised, will default to Active`)

    const budget = raw['budget'] ? parseFloat(raw['budget']) : undefined
    if (raw['budget'] && isNaN(budget!)) errors.push('budget must be a number')

    const partnerName = normText(raw['partner_name'] || '')
    let partnerId: string | undefined
    if (partnerName) {
      partnerId = partnerMap.get(partnerName.toLowerCase())
      if (!partnerId) warnings.push(`Partner "${partnerName}" not found — partner will not be linked`)
    }

    const resolved = {
      title,
      customer_id: customerId,
      start_date: startDate,
      end_date: endDate,
      currency: normCurrency(raw['currency'] || 'USD'),
      project_type: (matchedType || 'Implementation') as any,
      status: (matchedStatus || 'Active') as any,
      budget: budget ?? null,
      description: normText(raw['description'] || ''),
      technology: normText(raw['technology'] || ''),
      partner_id: partnerId ?? null,
      project_id: '',
    }

    return { rowIndex: i + 4, raw, errors, warnings, resolved, status: errors.length ? 'error' : warnings.length ? 'warning' : 'valid' }
  })
}

export async function validateTaskRows(
  rawRows: Record<string, string>[],
  orgId: string,
  projects: Project[]
): Promise<ParsedRow[]> {
  const projectMap = new Map(projects.map(p => [p.title.toLowerCase(), p]))
  const rowRefMap = new Map<string, number>() // row_ref -> index

  // Build ref map first
  rawRows.forEach((raw, i) => {
    const ref = normText(raw['row_ref'] || '')
    if (ref) rowRefMap.set(ref.toLowerCase(), i)
  })

  return rawRows.map((raw, i) => {
    const errors: string[] = []
    const warnings: string[] = []

    const title = normText(raw['title'] || '')
    if (!title) errors.push('title is required')

    const projectTitle = normText(raw['project_title'] || '')
    let project: Project | undefined
    if (!projectTitle) {
      errors.push('project_title is required')
    } else {
      project = projectMap.get(projectTitle.toLowerCase())
      if (!project) errors.push(`Project "${projectTitle}" not found — create it first or check spelling`)
    }

    const status = normText(raw['status'] || '').toLowerCase()
    if (!status) errors.push('status is required')
    else if (!TASK_STATUSES.includes(status)) errors.push(`status must be one of: ${TASK_STATUSES.join(', ')}`)

    const priority = normText(raw['priority'] || '').toLowerCase()
    if (!priority) errors.push('priority is required')
    else if (!TASK_PRIORITIES.includes(priority)) errors.push(`priority must be one of: ${TASK_PRIORITIES.join(', ')}`)

    const taskType = normText(raw['task_type'] || '').toLowerCase()
    const matchedType = taskType ? TASK_TYPES.find(t => t === taskType) : 'task'
    if (taskType && !matchedType) warnings.push(`task_type "${taskType}" not recognised, will default to "task"`)

    const startDate = normDate(raw['start_date'] || '')
    const dueDate = normDate(raw['due_date'] || '')

    if (project) {
      if (startDate && startDate < project.start_date) errors.push(`start_date must be on or after project start date (${project.start_date})`)
      if (startDate && startDate > project.end_date) errors.push(`start_date must be on or before project end date (${project.end_date})`)
      if (dueDate && dueDate < project.start_date) errors.push(`due_date must be on or after project start date (${project.start_date})`)
      if (dueDate && dueDate > project.end_date) errors.push(`due_date must be on or before project end date (${project.end_date})`)
    }

    if (startDate && dueDate && startDate > dueDate) errors.push('start_date must be before due_date')

    const estHours = raw['estimated_hours'] ? parseFloat(raw['estimated_hours']) : undefined
    if (raw['estimated_hours'] && isNaN(estHours!)) errors.push('estimated_hours must be a number')

    const parentRef = normText(raw['parent_row_ref'] || '')
    let parentDepth = 0
    if (parentRef) {
      const parentIdx = rowRefMap.get(parentRef.toLowerCase())
      if (parentIdx === undefined) {
        warnings.push(`parent_row_ref "${parentRef}" not found in this file — task will be created as root task`)
      } else {
        // Check depth chain
        let checkRaw = rawRows[parentIdx]
        let depth = 1
        while (checkRaw) {
          const grandRef = normText(checkRaw['parent_row_ref'] || '')
          if (!grandRef) break
          const grandIdx = rowRefMap.get(grandRef.toLowerCase())
          if (grandIdx === undefined) break
          checkRaw = rawRows[grandIdx]
          depth++
          if (depth >= 3) {
            errors.push(`Task hierarchy would exceed maximum depth of 3 levels`)
            break
          }
        }
        parentDepth = depth
      }
    }

    const resolved = {
      title,
      project_id: project?.id ?? null,
      project_start: project?.start_date,
      project_end: project?.end_date,
      org_id: orgId,
      status: TASK_STATUSES.includes(status) ? status : 'todo',
      priority: TASK_PRIORITIES.includes(priority) ? priority : 'medium',
      task_type: (matchedType || 'task') as any,
      start_date: startDate,
      due_date: dueDate,
      description: normText(raw['description'] || ''),
      estimated_minutes: estHours !== undefined && !isNaN(estHours) ? Math.round(estHours * 60) : undefined,
      parent_row_ref: parentRef,
      row_ref: normText(raw['row_ref'] || ''),
      _parentDepth: parentDepth,
    }

    return { rowIndex: i + 4, raw, errors, warnings, resolved, status: errors.length ? 'error' : warnings.length ? 'warning' : 'valid' }
  })
}

// ── Bulk insert ───────────────────────────────────────────────────────────────

const BATCH_SIZE = 50

async function sleep(ms: number) { return new Promise(r => setTimeout(r, ms)) }

export async function insertRoleTypeRows(
  rows: ParsedRow[],
  orgId: string,
  userId: string,
  onProgress: (done: number, total: number) => void
): Promise<BulkUploadResult> {
  const valid = rows.filter(r => r.status !== 'error')
  const result: BulkUploadResult = { succeeded: 0, failed: 0, failedRows: [] }

  for (let i = 0; i < valid.length; i++) {
    const row = valid[i]
    try {
      await dataService.createResourceRoleType(orgId, { ...row.resolved, last_modified_by: userId } as any)
      result.succeeded++
    } catch (err: any) {
      result.failed++
      result.failedRows.push({ rowIndex: row.rowIndex, title: row.resolved?.title ?? '', error: err?.message ?? 'Insert failed' })
    }
    if ((i + 1) % BATCH_SIZE === 0) await sleep(200)
    onProgress(i + 1, valid.length)
  }
  return result
}

export async function insertResourceRows(
  rows: ParsedRow[],
  orgId: string,
  userId: string,
  onProgress: (done: number, total: number) => void
): Promise<BulkUploadResult> {
  const valid = rows.filter(r => r.status !== 'error')
  const result: BulkUploadResult = { succeeded: 0, failed: 0, failedRows: [] }

  for (let i = 0; i < valid.length; i++) {
    const row = valid[i]
    try {
      await dataService.createResource(orgId, { ...row.resolved, last_modified_by: userId } as any)
      result.succeeded++
    } catch (err: any) {
      result.failed++
      result.failedRows.push({ rowIndex: row.rowIndex, title: row.resolved?.title ?? '', error: err?.message ?? 'Insert failed' })
    }
    if ((i + 1) % BATCH_SIZE === 0) await sleep(200)
    onProgress(i + 1, valid.length)
  }
  return result
}

export async function insertCustomerRows(
  rows: ParsedRow[],
  orgId: string,
  userId: string,
  onProgress: (done: number, total: number) => void
): Promise<BulkUploadResult> {
  const valid = rows.filter(r => r.status !== 'error')
  const result: BulkUploadResult = { succeeded: 0, failed: 0, failedRows: [] }

  for (let i = 0; i < valid.length; i++) {
    const row = valid[i]
    try {
      await dataService.createCustomer(orgId, { ...row.resolved, last_modified_by: userId } as any)
      result.succeeded++
    } catch (err: any) {
      result.failed++
      result.failedRows.push({ rowIndex: row.rowIndex, title: row.resolved?.title ?? '', error: err?.message ?? 'Insert failed' })
    }
    if ((i + 1) % BATCH_SIZE === 0) await sleep(200)
    onProgress(i + 1, valid.length)
  }
  return result
}

export async function insertProjectRows(
  rows: ParsedRow[],
  orgId: string,
  userId: string,
  onProgress: (done: number, total: number) => void
): Promise<BulkUploadResult> {
  const valid = rows.filter(r => r.status !== 'error')
  const result: BulkUploadResult = { succeeded: 0, failed: 0, failedRows: [] }

  for (let i = 0; i < valid.length; i++) {
    const row = valid[i]
    try {
      // Auto-generate project_id if not provided
      const projectId = row.resolved?.project_id || generateProjectId(row.resolved?.title, row.resolved?.project_type)
      await dataService.createProject(orgId, { ...row.resolved, project_id: projectId, last_modified_by: userId } as any)
      result.succeeded++
    } catch (err: any) {
      result.failed++
      result.failedRows.push({ rowIndex: row.rowIndex, title: row.resolved?.title ?? '', error: err?.message ?? 'Insert failed' })
    }
    if ((i + 1) % BATCH_SIZE === 0) await sleep(200)
    onProgress(i + 1, valid.length)
  }
  return result
}

function generateProjectId(title: string, type: string): string {
  const prefix = (title || 'PRJ').slice(0, 3).toUpperCase().replace(/\s/g, '')
  const typeAbbr = ({ Implementation: 'IMP', Support: 'SUP', 'T&M': 'TM' } as any)[type] || 'PRJ'
  const year = new Date().getFullYear()
  const rand = Math.floor(Math.random() * 9000) + 1000
  return `${prefix}-${typeAbbr}-${year}-${rand}`
}

export async function insertTaskRows(
  rows: ParsedRow[],
  orgId: string,
  userId: string,
  onProgress: (done: number, total: number) => void
): Promise<BulkUploadResult> {
  const valid = rows.filter(r => r.status !== 'error')
  const result: BulkUploadResult = { succeeded: 0, failed: 0, failedRows: [] }

  // Topological sort: parents before children
  const rowRefToInserted = new Map<string, string>() // row_ref -> inserted task id
  const sorted = topologicalSort(valid)

  // Track order_index per project
  const projectOrderMap = new Map<string, number>()

  for (let i = 0; i < sorted.length; i++) {
    const row = sorted[i]
    const res = row.resolved!
    try {
      const projectId = res.project_id
      if (!projectOrderMap.has(projectId)) projectOrderMap.set(projectId, 0)
      const orderIndex = projectOrderMap.get(projectId)!
      projectOrderMap.set(projectId, orderIndex + 1)

      // Resolve parent_task_id from batch inserts
      let parentTaskId: string | undefined
      if (res.parent_row_ref) {
        parentTaskId = rowRefToInserted.get(res.parent_row_ref.toLowerCase())
      }

      const task = await taskService.createTask({
        org_id: orgId,
        project_id: projectId,
        title: res.title,
        description: res.description,
        task_type: res.task_type,
        status: res.status,
        priority: res.priority,
        start_date: res.start_date || undefined,
        due_date: res.due_date || undefined,
        estimated_minutes: res.estimated_minutes,
        parent_task_id: parentTaskId,
        order_index: orderIndex,
        created_by: userId,
        last_modified_by: userId,
      } as any)

      if (res.row_ref) rowRefToInserted.set(res.row_ref.toLowerCase(), task.id)
      result.succeeded++
    } catch (err: any) {
      result.failed++
      result.failedRows.push({ rowIndex: row.rowIndex, title: res.title ?? '', error: err?.message ?? 'Insert failed' })
    }
    if ((i + 1) % BATCH_SIZE === 0) await sleep(200)
    onProgress(i + 1, sorted.length)
  }
  return result
}

function topologicalSort(rows: ParsedRow[]): ParsedRow[] {
  const refToRow = new Map<string, ParsedRow>()
  rows.forEach(r => {
    const ref = r.resolved?.row_ref?.toLowerCase()
    if (ref) refToRow.set(ref, r)
  })

  const sorted: ParsedRow[] = []
  const visited = new Set<number>()

  function visit(row: ParsedRow) {
    if (visited.has(row.rowIndex)) return
    visited.add(row.rowIndex)
    const parentRef = row.resolved?.parent_row_ref?.toLowerCase()
    if (parentRef && refToRow.has(parentRef)) {
      visit(refToRow.get(parentRef)!)
    }
    sorted.push(row)
  }

  rows.forEach(r => visit(r))
  return sorted
}

// ── Error report generation ───────────────────────────────────────────────────

export function generateErrorReport(
  originalRows: Record<string, string>[],
  failedRows: BulkUploadResult['failedRows'],
  entityType: EntityType
): void {
  const failMap = new Map(failedRows.map(f => [f.rowIndex, f.error]))

  const headers = originalRows.length > 0 ? Object.keys(originalRows[0]) : []
  const outputHeaders = [...headers, 'Import Error']

  const data = originalRows.map((row, i) => {
    const rowIndex = i + 4 // matches rowIndex in ParsedRow
    const err = failMap.get(rowIndex) ?? ''
    return [...headers.map(h => row[h] ?? ''), err]
  })

  const ws = XLSX.utils.aoa_to_sheet([outputHeaders, ...data])
  ws['!cols'] = outputHeaders.map(() => ({ wch: 22 }))

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Errors')
  XLSX.writeFile(wb, `${entityType}_import_errors.xlsx`)
}
